import { isTimePermissionEnabled, isTimePermissionEntity } from '../config/applicationFeatures';
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { AppCard } from './AppCard';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import {
  getHrAdminApprovalFields,
  isLeaveApprovalType,
  isPermissionApprovalType,
  isWfhApprovalType,
} from '../utils/hrAdminApprovalFields';
import { getWorkflowRequestTypeLabel } from '../utils/workflowRequestType';

type Props = {
  requestTypeCode: string | null;
  requestDetails: Record<string, unknown> | null;
};

function Field({ label, value }: { label: string; value: string }) {
  const palette = useAppColors();
  return (
    <View style={styles.field}>
      <Text style={[styles.label, { color: palette.textMuted }]}>{label}</Text>
      <Text style={[styles.value, { color: palette.textPrimary }]}>
        {value}
      </Text>
    </View>
  );
}

export function WorkflowRequestDetails({
  requestTypeCode,
  requestDetails,
}: Props) {
  const palette = useAppColors();
  const typeLabel = getWorkflowRequestTypeLabel(requestTypeCode);
  if (!isTimePermissionEnabled() && isTimePermissionEntity(requestTypeCode)) return null;
  const isKnownType =
    isLeaveApprovalType(requestTypeCode) ||
    isWfhApprovalType(requestTypeCode) ||
    isPermissionApprovalType(requestTypeCode);

  if (!isKnownType || !requestDetails || !Object.keys(requestDetails).length) {
    return (
      <AppCard style={styles.card}>
        <Text style={[styles.section, { color: palette.textPrimary }]}>
          Request details
        </Text>
        <Text style={[styles.help, { color: palette.textSecondary }]}>
          Request details are not available for this request type.
        </Text>
      </AppCard>
    );
  }

  const fields = getHrAdminApprovalFields(
    {
      workflowInstanceId: 0,
      requestTypeCode,
      requestTypeLabel: typeLabel,
      requestReferenceId: null,
      requestNumber: null,
      employee: {
        employeeId: null,
        employeeNumber: null,
        displayName: null,
        departmentName: null,
        designationName: null,
      },
      workflowStatusCode: null,
      workflowStatusLabel: '',
      submittedAt: null,
      completedAt: null,
      currentStep: null,
      steps: [],
      requestDetails,
      canApprove: false,
      canReject: false,
    },
    { includeShared: false },
  );

  return (
    <AppCard style={styles.card}>
      <Text style={[styles.section, { color: palette.textPrimary }]}>
        {isLeaveApprovalType(requestTypeCode)
          ? 'Leave Details'
          : isWfhApprovalType(requestTypeCode)
            ? 'WFH Details'
            : 'Permission Details'}
      </Text>
      {fields.map(field => (
        <Field key={field.label} label={field.label} value={field.value} />
      ))}
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: { gap: spacing.sm, marginBottom: spacing.md },
  section: { ...typography.sectionTitle },
  field: { gap: 2 },
  label: { ...typography.caption },
  value: { ...typography.body },
  help: { ...typography.body },
});
